"use client";

// # FILE: src/shared/hooks/use-api-error.ts
// # VERSION: 1.0.0
// # START_MODULE_CONTRACT:
// # PURPOSE: Хук для обработки ошибок API запросов с автоматическим отображением уведомлений.
// # SCOPE: Обработка ошибок Supabase и других API.
// # INPUT: Ошибки от API.
// # OUTPUT: Обработанные ошибки и функции для их обработки.
// # KEYWORDS: [DOMAIN(9): ErrorHandling; TECH(8): ReactHooks; PATTERN(7): ErrorHandling]
// # END_MODULE_CONTRACT

import { useCallback } from "react";
import { toast } from "@oxpa/shared/hooks/use-toast";
import { PostgrestError } from "@supabase/supabase-js";

// START_TYPE_ApiError
// START_CONTRACT:
// PURPOSE: Типизированная структура ошибки API.
// END_CONTRACT
export type ApiError = {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
};
// END_TYPE_ApiError

// START_TYPE_ErrorHandlerOptions
// START_CONTRACT:
// PURPOSE: Опции для обработчика ошибок.
// END_CONTRACT
export type ErrorHandlerOptions = {
  showToast?: boolean;
  customMessage?: string;
  fallbackMessage?: string;
  onError?: (error: ApiError) => void;
  logError?: boolean;
};
// END_TYPE_ErrorHandlerOptions

// START_ERROR_MESSAGES_MAP
// START_CONTRACT:
// PURPOSE: Карта сообщений об ошибках для различных кодов PostgreSQL.
// END_CONTRACT
const ERROR_MESSAGES: Record<string, string> = {
  // Ошибки авторизации
  "PGRST301": "Ошибка авторизации. Пожалуйста, войдите снова.",
  "PGRST302": "Доступ запрещен. У вас недостаточно прав.",
  "42501": "Доступ запрещен (RLS). Обратитесь к администратору.",
  
  // Ошибки уникальности
  "23505": "Запись с такими данными уже существует.",
  
  // Ошибки внешних ключей
  "23503": "Невозможно выполнить операцию: запись связана с другими данными.",
  
  // Ошибки ограничений
  "23514": "Нарушение ограничения проверки.",
  "23502": "Обязательное поле не заполнено.",
  
  // Ошибки соединения
  "ECONNREFUSED": "Не удалось подключиться к серверу. Проверьте подключение к интернету.",
  "ETIMEDOUT": "Время ожидания соединения истекло. Попробуйте снова.",
  
  // Стандартные ошибки
  "default": "Произошла ошибка. Пожалуйста, попробуйте позже.",
};
// END_ERROR_MESSAGES_MAP

// START_FUNCTION_parseSupabaseError
// START_CONTRACT:
// PURPOSE: Парсит ошибку Supabase в стандартный формат.
// INPUTS:
// - [Ошибка Supabase] => error: PostgrestError | Error | null
// OUTPUTS:
// - ApiError | null - Структурированная ошибка или null
// END_CONTRACT
export function parseSupabaseError(
  error: PostgrestError | Error | null | undefined
): ApiError | null {
  // START_BLOCK_CHECK_NULL
  if (!error) {
    return null;
  }
  // END_BLOCK_CHECK_NULL

  // START_BLOCK_PARSE_POSTGREST_ERROR
  if ("code" in error && typeof error.code === "string") {
    const pgError = error as PostgrestError;
    return {
      message: pgError.message,
      code: pgError.code,
      details: pgError.details,
      hint: pgError.hint,
    };
  }
  // END_BLOCK_PARSE_POSTGREST_ERROR

  // START_BLOCK_PARSE_STANDARD_ERROR
  return {
    message: error.message,
    code: "UNKNOWN",
    details: error.stack,
  };
  // END_BLOCK_PARSE_STANDARD_ERROR
}
// END_FUNCTION_parseSupabaseError

// START_FUNCTION_getErrorMessage
// START_CONTRACT:
// PURPOSE: Получает человекочитаемое сообщение об ошибке по коду.
// INPUTS:
// - [Ошибка] => error: ApiError | null
// - [Сообщение по умолчанию] => fallback: string
// OUTPUTS:
// - string - Сообщение об ошибке
// END_CONTRACT
export function getErrorMessage(
  error: ApiError | null,
  fallback: string = ERROR_MESSAGES.default
): string {
  // START_BLOCK_CHECK_NULL
  if (!error) {
    return fallback;
  }
  // END_BLOCK_CHECK_NULL

  // START_BLOCK_GET_MESSAGE_BY_CODE
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  // END_BLOCK_GET_MESSAGE_BY_CODE

  // START_BLOCK_RETURN_MESSAGE
  return error.message || fallback;
  // END_BLOCK_RETURN_MESSAGE
}
// END_FUNCTION_getErrorMessage

// START_HOOK_useApiError
// START_CONTRACT:
// PURPOSE: Хук для обработки API ошибок с toast-уведомлениями.
// OUTPUTS:
// - Object - Объект с методами для обработки ошибок
// END_CONTRACT
export function useApiError() {
  // START_METHOD_handleError
  // START_CONTRACT:
  // PURPOSE: Обрабатывает ошибку API с опциональным отображением toast.
  // INPUTS:
  // - [Ошибка] => error: PostgrestError | Error | null
  // - [Опции] => options: ErrorHandlerOptions
  // OUTPUTS:
  // - ApiError | null - Обработанная ошибка
  // SIDE_EFFECTS:
  // - Отображение toast-уведомления (если включено)
  // - Логирование ошибки (если включено)
  // END_CONTRACT
  const handleError = useCallback(
    (
      error: PostgrestError | Error | null | undefined,
      options: ErrorHandlerOptions = {}
    ): ApiError | null => {
      const {
        showToast = true,
        customMessage,
        fallbackMessage = ERROR_MESSAGES.default,
        onError,
        logError = true,
      } = options;

      // START_BLOCK_PARSE_ERROR
      const parsedError = parseSupabaseError(error);
      // END_BLOCK_PARSE_ERROR

      // START_BLOCK_CHECK_NULL
      if (!parsedError) {
        return null;
      }
      // END_BLOCK_CHECK_NULL

      // START_BLOCK_LOG_ERROR
      if (logError) {
        console.error("[useApiError] Ошибка API:", parsedError);
      }
      // END_BLOCK_LOG_ERROR

      // START_BLOCK_PREPARE_MESSAGE
      const message = customMessage || getErrorMessage(parsedError, fallbackMessage);
      // END_BLOCK_PREPARE_MESSAGE

      // START_BLOCK_SHOW_TOAST
      if (showToast) {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description: message,
        });
      }
      // END_BLOCK_SHOW_TOAST

      // START_BLOCK_CALLBACK
      if (onError) {
        onError(parsedError);
      }
      // END_BLOCK_CALLBACK

      return parsedError;
    },
    []
  );
  // END_METHOD_handleError

  // START_METHOD_handleSuccess
  // START_CONTRACT:
  // PURPOSE: Показывает toast с сообщением об успехе.
  // INPUTS:
  // - [Сообщение] => message: string
  // - [Заголовок] => title: string
  // SIDE_EFFECTS:
  // - Отображение toast-уведомления
  // END_CONTRACT
  const handleSuccess = useCallback(
    (message: string, title: string = "Успешно"): void => {
      toast({
        title,
        description: message,
      });
    },
    []
  );
  // END_METHOD_handleSuccess

  // START_METHOD_wrapAsync
  // START_CONTRACT:
  // PURPOSE: Обертывает асинхронную функцию для автоматической обработки ошибок.
  // INPUTS:
  // - [Асинхронная функция] => fn: (...args: T) => Promise<R>
  // - [Опции] => options: ErrorHandlerOptions
  // OUTPUTS:
  // - (...args: T) => Promise<R | null> - Обернутая функция
  // END_CONTRACT
  const wrapAsync = useCallback(
    <T extends any[], R>(
      fn: (...args: T) => Promise<R>,
      options: ErrorHandlerOptions = {}
    ): ((...args: T) => Promise<R | null>) => {
      return async (...args: T): Promise<R | null> => {
        try {
          return await fn(...args);
        } catch (error) {
          handleError(error as Error, options);
          return null;
        }
      };
    },
    [handleError]
  );
  // END_METHOD_wrapAsync

  return {
    handleError,
    handleSuccess,
    wrapAsync,
    parseSupabaseError,
    getErrorMessage,
  };
}
// END_HOOK_useApiError

// START_HOOK_useAsyncState
// START_CONTRACT:
// PURPOSE: Хук для управления состоянием асинхронной операции.
// OUTPUTS:
// - Object - Состояние и методы для управления загрузкой
// END_CONTRACT
import { useState, useCallback as useCallbackState } from "react";

export type AsyncState<T = unknown> = {
  data: T | null;
  isLoading: boolean;
  error: ApiError | null;
};

export function useAsyncState<T = unknown>(initialData: T | null = null) {
  const [state, setState] = useState<AsyncState<T>>({
    data: initialData,
    isLoading: false,
    error: null,
  });

  const setLoading = useCallbackState((isLoading: boolean) => {
    setState((prev) => ({ ...prev, isLoading }));
  }, []);

  const setData = useCallbackState((data: T | null) => {
    setState((prev) => ({ ...prev, data, error: null }));
  }, []);

  const setError = useCallbackState((error: ApiError | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const reset = useCallbackState(() => {
    setState({ data: initialData, isLoading: false, error: null });
  }, [initialData]);

  return {
    ...state,
    setLoading,
    setData,
    setError,
    reset,
  };
}
// END_HOOK_useAsyncState
